"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useMemo, useRef } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AiChartLegendContent,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
  resolveAiLegendLayout,
} from "./ai-chart";
import { resolveBarSeparatorStyle } from "./bar-presentation";
import { renderSquareZeroSafeBarShape } from "./bar-shape";
import {
  DEFAULT_EXPORT_FORMATS,
  EMPTY_VIZ_ROWS,
  EMPTY_VIZ_SERIES,
  ensurePngExportFormats,
  formatPercentTick,
  formatValueTick,
  formatVizTooltipMetricValue,
  isRecord,
  isVizAxisReversed,
  normalizeVizExportFormats,
  resolveEffectiveXAxisTickDensity,
  resolveVizActiveDotProps,
  resolveVizAxisLineProps,
  resolveVizAxisStyle,
  resolveVizAxisTickLineProps,
  resolveVizCartesianGridProps,
  resolveVizPaletteMode,
  resolveVizReferenceAreas,
  resolveVizReferenceLabelProps,
  resolveVizReferenceLines,
  resolveVizSemanticReferenceLineProps,
  resolveVizSeriesColor,
  resolveVizTooltipMode,
  resolveXAxisTickDensityConfig,
  resolveXAxisTickLayout,
  type VizAxisDirections,
  type VizAxisPreset,
  type VizPaletteMode,
  type VizTooltipMode,
  type VizXAxisTickDensity,
} from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
} from "./dataset-fields";
import {
  formatVizAxisDateLabel,
  formatVizTooltipDomainLabel,
  isIsoDateLike,
} from "./date-format";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import { resolveDisplayLabel } from "./label-resolver";
import {
  buildTimeSeriesFinitePointCountMap,
  hasRenderableTimeSeriesValue,
  normalizeRowsForAxis,
  resolveTimeSeriesAxisOuterMargin,
  resolveTimeSeriesAxisState,
  resolveTimeSeriesNumericAxisWidth,
  resolveTimeSeriesStaticDotProps,
  resolveTooltipPercentFields,
} from "./time-series-core";
import { resolveTimeSeriesLineInterpolation } from "./time-series-line-utils";
import {
  resolveTimeSeriesChartHeight,
  TimeSeriesActionBar,
  useTimeSeriesVisibleSeries,
} from "./time-series-shared";
import { type TimeSeriesYAxisMode } from "./y-axis-mode";

export type TimeSeriesComposedProps = BaseComponentProps<{
  dataPath: string;
  xKey: string;
  lineSeries?: Array<{
    key: string;
    label?: string | null;
    color?: string | null;
  }>;
  barSeries?: Array<{
    key: string;
    label?: string | null;
    color?: string | null;
  }>;
  xLabel?: string | null;
  seriesLabels?: Record<string, string> | null;
  periodLabels?: {
    current?: string | null;
    comparison?: string | null;
  } | null;
  chartHeight?: number | null;
  axisPreset?: VizAxisPreset | null;
  tooltipMode?: VizTooltipMode | null;
  paletteMode?: VizPaletteMode | null;
  yAxisMode?: TimeSeriesYAxisMode | null;
  axisDirections?: VizAxisDirections | null;
  stacking?: "grouped" | "stacked" | null;
  xAxisTicks?: { density?: VizXAxisTickDensity | null } | null;
  referenceLines?: Array<{
    value: number;
    label?: string | null;
    color?: string | null;
  }> | null;
  referenceAreas?: Array<{
    from: number;
    to: number;
    label?: string | null;
    color?: string | null;
    opacity?: number | null;
  }> | null;
  allowSeriesToggle?: boolean | null;
  seriesToggleMode?: "single-metric" | "multi-metric" | null;
  defaultVisibleSeriesKeys?: string[] | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
}>;

type SeriesKind = "line" | "bar";

type ComposedSeriesEntry = {
  key: string;
  label: string | null;
  color: string | null;
  kind: SeriesKind;
};

/**
 * 線と棒を混在表示する時系列チャートを描画する。
 *
 * @remarks
 * component props の style preset だけで視認性を揃えることで、共通 chart UI の
 * 既存見た目を壊さずに AI チャート側の品質を独立改善できるようにする。
 */
export function TimeSeriesComposed({ props }: TimeSeriesComposedProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const {
    dataPath,
    xKey,
    lineSeries,
    barSeries,
    seriesLabels,
    chartHeight,
    axisPreset,
    tooltipMode,
    paletteMode,
    yAxisMode,
    axisDirections,
    stacking,
    xAxisTicks,
    referenceLines,
    referenceAreas,
    allowSeriesToggle,
    seriesToggleMode,
    defaultVisibleSeriesKeys,
    allowExport,
    exportFormats,
  } = props;
  const resolvedTooltipMode = resolveVizTooltipMode(tooltipMode);
  const resolvedPaletteMode = resolveVizPaletteMode(paletteMode);
  const rawData = useStateValue<unknown>(dataPath);
  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return EMPTY_VIZ_ROWS;
    return rawData.filter(isRecord);
  }, [rawData]);

  const lineSeriesList = Array.isArray(lineSeries)
    ? lineSeries
    : EMPTY_VIZ_SERIES;
  const barSeriesList = Array.isArray(barSeries) ? barSeries : EMPTY_VIZ_SERIES;

  const allSeries = useMemo<ComposedSeriesEntry[]>(
    () => [
      ...barSeriesList.map((entry) => ({
        key: entry.key,
        label: entry.label ?? null,
        color: entry.color ?? null,
        kind: "bar" as const,
      })),
      ...lineSeriesList.map((entry) => ({
        key: entry.key,
        label: entry.label ?? null,
        color: entry.color ?? null,
        kind: "line" as const,
      })),
    ],
    [barSeriesList, lineSeriesList],
  );

  const allSeriesKeys = useMemo(
    () => allSeries.map((entry) => entry.key),
    [allSeries],
  );
  const { visibleSeriesKeySet, toggleSeriesVisibility } =
    useTimeSeriesVisibleSeries({
      allSeriesKeys,
      defaultVisibleSeriesKeys,
      seriesToggleMode,
    });

  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );
  const finitePointCountBySeriesKey = useMemo(() => {
    return buildTimeSeriesFinitePointCountMap({
      rows: data,
      seriesKeys: allSeriesKeys,
    });
  }, [allSeriesKeys, data]);
  const renderableAllSeries = useMemo(() => {
    return allSeries.filter((entry) =>
      hasRenderableTimeSeriesValue(finitePointCountBySeriesKey.get(entry.key)),
    );
  }, [allSeries, finitePointCountBySeriesKey]);
  const effectiveSeries = useMemo(() => {
    return renderableAllSeries.filter((entry) =>
      visibleSeriesKeySet.has(entry.key),
    );
  }, [renderableAllSeries, visibleSeriesKeySet]);
  const percentKeySet = useMemo(
    () =>
      new Set(
        effectiveSeries
          .map((entry) => entry.key)
          .filter((key) => isPercentFieldKey(key, metaByName)),
      ),
    [effectiveSeries, metaByName],
  );

  const chartConfig = useMemo(() => {
    return Object.fromEntries(
      allSeries.map((entry, index) => [
        entry.key,
        {
          label:
            resolveDisplayLabel(
              seriesLabels?.[entry.key],
              entry.label,
              metaByName.get(entry.key)?.label,
              entry.key,
            ) || entry.key,
          color: resolveVizSeriesColor({
            index,
            color: entry.color,
            paletteMode: resolvedPaletteMode,
          }),
        },
      ]),
    );
  }, [allSeries, metaByName, resolvedPaletteMode, seriesLabels]);

  const axisState = useMemo(
    () =>
      resolveTimeSeriesAxisState({
        effectiveSeriesKeys: effectiveSeries.map((entry) => entry.key),
        percentKeySet,
        yAxisMode,
      }),
    [effectiveSeries, percentKeySet, yAxisMode],
  );
  const {
    axisSeriesKeys,
    hasPercentSeries,
    hasValueSeries,
    useDualAxis,
    useNormalizedAxis,
  } = axisState;

  const chartData = useMemo(() => {
    return normalizeRowsForAxis({
      rows: data,
      axisSeriesKeys,
      useNormalizedAxis,
    });
  }, [axisSeriesKeys, data, useNormalizedAxis]);
  const leftAxisSeriesKeys = useMemo(
    () => axisSeriesKeys.filter((key) => !percentKeySet.has(key)),
    [axisSeriesKeys, percentKeySet],
  );
  const rightAxisSeriesKeys = useMemo(
    () => axisSeriesKeys.filter((key) => percentKeySet.has(key)),
    [axisSeriesKeys, percentKeySet],
  );

  const resolvedReferenceLines = useMemo(
    () => resolveVizReferenceLines(referenceLines),
    [referenceLines],
  );
  const resolvedReferenceAreas = useMemo(
    () => resolveVizReferenceAreas(referenceAreas),
    [referenceAreas],
  );

  const xAxisTickDensity = resolveEffectiveXAxisTickDensity({
    density: xAxisTicks?.density ?? null,
    isMobile,
  });
  const xAxisTickConfig = resolveXAxisTickDensityConfig({
    density: xAxisTickDensity,
    rowCount: data.length,
    baseMinTickGap: isMobile ? 18 : 22,
  });
  const axisStyle = useMemo(
    () =>
      resolveVizAxisStyle({
        preset: axisPreset,
        isMobile,
      }),
    [axisPreset, isMobile],
  );
  const leftYAxisWidth = useMemo(
    () =>
      resolveTimeSeriesNumericAxisWidth({
        rows: chartData,
        seriesKeys: useDualAxis ? leftAxisSeriesKeys : axisSeriesKeys,
        tickFormatter:
          !useNormalizedAxis && hasPercentSeries && !hasValueSeries
            ? formatPercentTick
            : formatValueTick,
        tickFontSize: axisStyle.tickFontSize,
        isMobile,
      }),
    [
      axisSeriesKeys,
      axisStyle.tickFontSize,
      chartData,
      hasPercentSeries,
      hasValueSeries,
      isMobile,
      leftAxisSeriesKeys,
      useDualAxis,
      useNormalizedAxis,
    ],
  );
  const rightYAxisWidth = useMemo(
    () =>
      useDualAxis
        ? resolveTimeSeriesNumericAxisWidth({
            rows: chartData,
            seriesKeys: rightAxisSeriesKeys,
            tickFormatter: formatPercentTick,
            tickFontSize: axisStyle.tickFontSize,
            isMobile,
          })
        : 0,
    [
      axisStyle.tickFontSize,
      chartData,
      isMobile,
      rightAxisSeriesKeys,
      useDualAxis,
    ],
  );
  const chartRightMargin = useMemo(() => {
    if (!useDualAxis) return 16;
    return resolveTimeSeriesAxisOuterMargin({
      axisWidth: rightYAxisWidth,
      isMobile,
    });
  }, [isMobile, rightYAxisWidth, useDualAxis]);
  const formatXAxisTick = useCallback(
    (value: unknown) => formatVizAxisDateLabel(String(value ?? ""), i18n),
    [i18n],
  );
  const rotateXAxis = useMemo(() => {
    return data.slice(0, 12).some((row) => {
      const raw = String(row[xKey] ?? "");
      const formatted = formatXAxisTick(row[xKey]);
      return isIsoDateLike(raw) || formatted.length > 10;
    });
  }, [data, formatXAxisTick, xKey]);
  const xAxisTickLayout = useMemo(() => {
    return resolveXAxisTickLayout({
      rotate: rotateXAxis,
      labels: data.slice(0, 12).map((row) => formatXAxisTick(row[xKey])),
    });
  }, [data, formatXAxisTick, rotateXAxis, xKey]);
  const resolvedChartHeight = resolveTimeSeriesChartHeight({
    chartHeight,
    hasDenseXAxis: rotateXAxis,
    isMobile,
  });
  const resolvedExportFormats = ensurePngExportFormats(
    normalizeVizExportFormats(exportFormats, DEFAULT_EXPORT_FORMATS),
  );
  const tooltipPercentFields = useMemo(
    () =>
      resolveTooltipPercentFields({
        axisSeriesKeys,
        percentKeySet,
        useNormalizedAxis,
      }),
    [axisSeriesKeys, percentKeySet, useNormalizedAxis],
  );
  const formatTooltipValue = useCallback(
    (value: unknown, key: string) =>
      formatVizTooltipMetricValue(value, {
        isPercent: tooltipPercentFields.includes(key),
      }),
    [tooltipPercentFields],
  );

  const handleExport = useCallback(
    async (format: "csv" | "json" | "png") => {
      const columns = [xKey, ...axisSeriesKeys];
      if (format === "csv") {
        downloadRowsAsCsv({
          rows: data,
          columns,
          baseName: "time-series-composed",
        });
        return;
      }
      if (format === "json") {
        downloadRowsAsJson({
          rows: data,
          columns,
          baseName: "time-series-composed",
        });
        return;
      }
      await downloadVizAsPng({
        element: containerRef.current,
        baseName: "time-series-composed",
      });
    },
    [axisSeriesKeys, data, xKey],
  );
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);

  const seriesMenuItems = useMemo(
    () =>
      renderableAllSeries.map((entry) => ({
        key: entry.key,
        label:
          resolveDisplayLabel(
            seriesLabels?.[entry.key],
            entry.label,
            metaByName.get(entry.key)?.label,
            entry.key,
          ) || entry.key,
      })),
    [metaByName, renderableAllSeries, seriesLabels],
  );
  const legendPayload = useMemo(
    () =>
      renderableAllSeries.map((entry) => ({
        dataKey: entry.key,
        value:
          resolveDisplayLabel(
            chartConfig[entry.key]?.label,
            entry.label,
            metaByName.get(entry.key)?.label,
            entry.key,
          ) || entry.key,
        color: chartConfig[entry.key]?.color ?? "var(--chart-1)",
      })),
    [chartConfig, metaByName, renderableAllSeries],
  );
  const legendLayout = useMemo(
    () =>
      resolveAiLegendLayout({
        itemCount: legendPayload.length,
        isMobile,
      }),
    [isMobile, legendPayload.length],
  );
  const barSeparatorStyle = useMemo(
    () => resolveBarSeparatorStyle({ stacking }),
    [stacking],
  );
  const isPrimaryYAxisReversed = isVizAxisReversed(axisDirections, "primary");
  const isSecondaryYAxisReversed = isVizAxisReversed(
    axisDirections,
    "secondary",
  );

  const hasChartData = data.length > 0 && effectiveSeries.length > 0;

  return (
    <div ref={containerRef} className="w-full">
      <TimeSeriesActionBar
        allowSeriesToggle={allowSeriesToggle}
        allowExport={allowExport}
        seriesMenuItems={seriesMenuItems}
        visibleKeySet={visibleSeriesKeySet}
        onToggleSeries={toggleSeriesVisibility}
        exportFormats={resolvedExportFormats}
        onExport={handleExport}
        onCopyImage={handleCopyImage}
        hasChartData={hasChartData}
      />
      <ChartContainer
        config={chartConfig}
        className="w-full rounded-[18px]"
        style={{ height: `${resolvedChartHeight}px` }}
      >
        <ComposedChart
          data={chartData}
          margin={{
            top: 18,
            right: chartRightMargin + 4,
            bottom: xAxisTickLayout.bottomMargin + 2,
            left: 2,
          }}
        >
          <CartesianGrid
            {...resolveVizCartesianGridProps({
              axisStyle,
              yAxisId: useDualAxis ? "value" : undefined,
            })}
          />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatXAxisTick}
            interval={xAxisTickConfig.interval}
            minTickGap={xAxisTickConfig.minTickGap}
            tickMargin={Math.max(
              xAxisTickLayout.tickMargin,
              axisStyle.tickMargin,
            )}
            angle={xAxisTickLayout.angle}
            textAnchor={xAxisTickLayout.textAnchor}
            height={xAxisTickLayout.height}
            tickLine={resolveVizAxisTickLineProps()}
            axisLine={resolveVizAxisLineProps()}
            tick={
              rotateXAxis
                ? {
                    dx: xAxisTickLayout.tickDx,
                    dy: xAxisTickLayout.tickDy,
                    fontSize: axisStyle.tickFontSize,
                  }
                : { fontSize: axisStyle.tickFontSize }
            }
          />
          {useDualAxis ? (
            <>
              <YAxis
                yAxisId="value"
                tickFormatter={(value) => formatValueTick(value)}
                tickMargin={axisStyle.tickMargin}
                width={leftYAxisWidth}
                tickLine={resolveVizAxisTickLineProps()}
                axisLine={resolveVizAxisLineProps()}
                tick={{ fontSize: axisStyle.tickFontSize }}
                reversed={isPrimaryYAxisReversed}
              />
              <YAxis
                yAxisId="percent"
                orientation="right"
                tickFormatter={(value) => formatPercentTick(value)}
                tickMargin={axisStyle.tickMargin}
                width={rightYAxisWidth}
                tickLine={resolveVizAxisTickLineProps()}
                axisLine={resolveVizAxisLineProps()}
                tick={{ fontSize: axisStyle.tickFontSize }}
                reversed={isSecondaryYAxisReversed}
              />
            </>
          ) : (
            <YAxis
              tickFormatter={(value) =>
                !useNormalizedAxis && hasPercentSeries && !hasValueSeries
                  ? formatPercentTick(value)
                  : formatValueTick(value)
              }
              tickMargin={axisStyle.tickMargin}
              width={leftYAxisWidth}
              tickLine={resolveVizAxisTickLineProps()}
              axisLine={resolveVizAxisLineProps()}
              tick={{ fontSize: axisStyle.tickFontSize }}
              domain={useNormalizedAxis ? [0, 100] : undefined}
              reversed={isPrimaryYAxisReversed}
            />
          )}
          {resolvedReferenceLines.map((line) => (
            <ReferenceLine
              key={line.key}
              y={line.value}
              {...resolveVizSemanticReferenceLineProps({ color: line.color })}
              label={resolveVizReferenceLabelProps({
                label: line.label,
                color: line.color,
              })}
            />
          ))}
          {resolvedReferenceAreas.map((area) => (
            <ReferenceArea
              key={area.key}
              y1={area.from}
              y2={area.to}
              fill={area.color}
              fillOpacity={area.opacity}
              label={resolveVizReferenceLabelProps({
                label: area.label,
                color: area.color,
                position: "insideTopLeft",
                dx: 8,
                dy: 6,
              })}
            />
          ))}
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelKey={xKey}
                labelClassName={
                  resolvedTooltipMode === "compact" ? "text-[11px]" : undefined
                }
                nameKey="dataKey"
                labelFormatter={(value) =>
                  formatVizTooltipDomainLabel(value, i18n)
                }
                formatToPercentFields={tooltipPercentFields}
                allowedKeys={axisSeriesKeys}
                orderKeys={axisSeriesKeys}
                valueFormatter={formatTooltipValue}
                resolveScaledValue={useNormalizedAxis}
              />
            }
          />
          {legendPayload.length > 0 ? (
            <ChartLegend
              content={(props) => (
                <AiChartLegendContent
                  className={legendLayout.className}
                  verticalAlign={props.verticalAlign}
                  payload={legendPayload}
                  visibleKeySet={visibleSeriesKeySet}
                  onToggleItem={
                    allowSeriesToggle !== false
                      ? toggleSeriesVisibility
                      : undefined
                  }
                />
              )}
              verticalAlign="bottom"
              height={legendLayout.height}
              wrapperStyle={legendLayout.wrapperStyle}
            />
          ) : null}
          {effectiveSeries.map((entry, index) => {
            const color =
              chartConfig[entry.key]?.color ??
              resolveVizSeriesColor({
                index,
                color: entry.color,
                paletteMode: resolvedPaletteMode,
              });
            const yAxisId = useDualAxis
              ? percentKeySet.has(entry.key)
                ? "percent"
                : "value"
              : undefined;
            if (entry.kind === "bar") {
              return (
                <Bar
                  key={entry.key}
                  dataKey={entry.key}
                  name={resolveDisplayLabel(
                    seriesLabels?.[entry.key],
                    entry.label,
                    metaByName.get(entry.key)?.label,
                    entry.key,
                  )}
                  fill={color}
                  shape={renderSquareZeroSafeBarShape}
                  stackId={stacking === "stacked" ? "viz-stack" : undefined}
                  yAxisId={yAxisId}
                  isAnimationActive={false}
                  animationBegin={0}
                  animationDuration={0}
                  stroke="hsl(var(--background))"
                  strokeOpacity={barSeparatorStyle.strokeOpacity}
                  strokeWidth={barSeparatorStyle.strokeWidth}
                />
              );
            }
            return (
              <Line
                key={entry.key}
                type={resolveTimeSeriesLineInterpolation({
                  dataKey: entry.key,
                  fieldMetaMap: metaByName,
                })}
                dataKey={entry.key}
                name={resolveDisplayLabel(
                  seriesLabels?.[entry.key],
                  entry.label,
                  metaByName.get(entry.key)?.label,
                  entry.key,
                )}
                stroke={color}
                dot={resolveTimeSeriesStaticDotProps({
                  finitePointCount: finitePointCountBySeriesKey.get(entry.key),
                  color,
                  radius: isMobile ? 2.5 : 3,
                })}
                strokeWidth={2.5}
                activeDot={resolveVizActiveDotProps({
                  radius: isMobile ? 3 : 4,
                  color,
                })}
                connectNulls
                yAxisId={yAxisId}
                isAnimationActive={false}
                animationBegin={0}
                animationDuration={0}
              />
            );
          })}
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
